import { KeyRound, Save, UserCog } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ROTULO_PAPEL, type Eu } from '@social/shared';
import { Botao, Campo, Entrada } from '../componentes/ui';
import { PaginaShell } from '../componentes/pagina-shell';
import { ErroApi } from '../lib/api';
import { api, useSessao } from '../lib/sessao';

/** O mesmo mínimo do autocadastro: é uma senha NASCENDO. */
const SENHA_MINIMA = 12;

/**
 * A CONTA — a pessoa cuidando dos próprios dados.
 *
 * ## O que ela deixa mudar, e o que não
 * Nome e senha. E-mail não: é a credencial de entrada, é única global, e
 * trocá-lo sem confirmar o endereço novo tranca a pessoa para fora na primeira
 * letra errada — este produto ainda não envia e-mail. Papel e escritório
 * aparecem em leitura, porque quem os muda é um administrador, noutro lugar.
 *
 * Mostrar os três em cinza, em vez de escondê-los, responde a pergunta que a
 * pessoa veio fazer ("posso mudar meu e-mail aqui?") sem ela ter de procurar.
 */
export function Conta(): JSX.Element {
  const eu = useSessao((s) => s.eu);
  const cache = useQueryClient();

  const [nome, setNome] = useState(eu?.nome ?? '');
  const [senhaAtual, setSenhaAtual] = useState('');
  const [senhaNova, setSenhaNova] = useState('');
  const [avisoPerfil, setAvisoPerfil] = useState<string | null>(null);
  const [avisoSenha, setAvisoSenha] = useState<string | null>(null);

  const perfil = useMutation({
    mutationFn: (dados: { nome: string }) => api.patch<Eu>('/eu', dados),
    onSuccess: async () => {
      setAvisoPerfil('Nome atualizado.');
      // A casca mostra o nome no canto: sem recarregar a sessão, a tela salva
      // e o cabeçalho continua com o nome velho até o próximo login.
      await useSessao.getState().recarregarEu();
      await cache.invalidateQueries({ queryKey: ['painel'] });
    },
  });

  const senha = useMutation({
    mutationFn: (dados: { senhaAtual: string; senhaNova: string }) =>
      api.post<{ acesso: string; refresh: string }>('/eu/senha', dados),
    onSuccess: async (sessao) => {
      /*
        A troca derruba TODOS os refresh do usuário — o desta aba incluído. O
        servidor devolve um par novo, e adotá-lo é o que impede a pessoa de ser
        expulsa por ter feito a coisa certa. Os outros aparelhos caem.
      */
      await useSessao.getState().adotarSessao(sessao);
      setAvisoSenha('Senha trocada. As sessões nos outros aparelhos foram encerradas.');
      setSenhaAtual('');
      setSenhaNova('');
    },
  });

  function salvarPerfil(e: FormEvent): void {
    e.preventDefault();
    setAvisoPerfil(null);
    perfil.mutate({ nome: nome.trim() });
  }

  function salvarSenha(e: FormEvent): void {
    e.preventDefault();
    setAvisoSenha(null);
    senha.mutate({ senhaAtual, senhaNova });
  }

  const nomeValido = nome.trim().length >= 3;
  const senhaCurta = senhaNova.length > 0 && senhaNova.length < SENHA_MINIMA;
  const podeTrocarSenha =
    senhaAtual.length > 0 && senhaNova.length >= SENHA_MINIMA && !senha.isPending;

  return (
    <PaginaShell
      titulo="Minha conta"
      subtitulo="Seus dados de acesso. Nome e senha você muda aqui."
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className="border-tinta-100 bg-superficie-2 flex flex-col gap-4 rounded-xl border p-4">
          <h2 className="text-tinta-900 flex items-center gap-2 font-semibold">
            <UserCog className="size-4" aria-hidden />
            Seus dados
          </h2>

          <form onSubmit={salvarPerfil} className="flex flex-col gap-3">
            <Campo rotulo="Nome" id="conta-nome" obrigatorio>
              <Entrada
                id="conta-nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                autoComplete="name"
              />
            </Campo>

            {/*
              Leitura, não campo. O `disabled` diz "existe, e não é aqui que
              muda" — esconder faria a pessoa procurar numa tela que não tem.
            */}
            <Campo
              rotulo="E-mail"
              id="conta-email"
              dica="É a sua credencial de entrada. A troca ainda não está disponível."
            >
              <Entrada id="conta-email" value={eu?.email ?? ''} disabled readOnly />
            </Campo>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Campo rotulo="Papel" id="conta-papel" dica="Muda com um administrador.">
                <Entrada
                  id="conta-papel"
                  value={eu ? ROTULO_PAPEL[eu.papel] : ''}
                  disabled
                  readOnly
                />
              </Campo>
              <Campo rotulo="Escritório" id="conta-escritorio">
                <Entrada
                  id="conta-escritorio"
                  value={eu?.escritorio.nome ?? ''}
                  disabled
                  readOnly
                />
              </Campo>
            </div>

            {perfil.isError && (
              <p role="alert" className="text-erro-600 text-sm">
                {perfil.error instanceof ErroApi
                  ? perfil.error.message
                  : 'Não foi possível salvar.'}
              </p>
            )}
            {avisoPerfil && (
              <p role="status" className="text-ok-600 text-sm">
                {avisoPerfil}
              </p>
            )}

            <div>
              <Botao type="submit" disabled={!nomeValido || perfil.isPending}>
                <Save className="size-4" aria-hidden />
                {perfil.isPending ? 'Salvando…' : 'Salvar'}
              </Botao>
            </div>
          </form>
        </section>

        <section className="border-tinta-100 bg-superficie-2 flex flex-col gap-4 rounded-xl border p-4">
          <h2 className="text-tinta-900 flex items-center gap-2 font-semibold">
            <KeyRound className="size-4" aria-hidden />
            Trocar senha
          </h2>

          <form onSubmit={salvarSenha} className="flex flex-col gap-3">
            {/*
              A senha ATUAL é pedida mesmo com sessão aberta: computador
              emprestado é o caso comum de escritório, e sem ela quem senta na
              cadeira troca a senha e toma a conta.
            */}
            <Campo rotulo="Senha atual" id="conta-senha-atual" obrigatorio>
              <Entrada
                id="conta-senha-atual"
                type="password"
                autoComplete="current-password"
                value={senhaAtual}
                onChange={(e) => setSenhaAtual(e.target.value)}
              />
            </Campo>

            <Campo
              rotulo="Senha nova"
              id="conta-senha-nova"
              obrigatorio
              dica={`Mínimo de ${String(SENHA_MINIMA)} caracteres.`}
            >
              <Entrada
                id="conta-senha-nova"
                type="password"
                autoComplete="new-password"
                minLength={SENHA_MINIMA}
                value={senhaNova}
                onChange={(e) => setSenhaNova(e.target.value)}
              />
            </Campo>

            {senhaCurta && (
              <p className="text-erro-600 text-sm" aria-live="polite">
                Faltam {SENHA_MINIMA - senhaNova.length} caracteres.
              </p>
            )}

            {senha.isError && (
              <p role="alert" className="text-erro-600 text-sm">
                {senha.error instanceof ErroApi
                  ? senha.error.message
                  : 'Não foi possível trocar a senha.'}
              </p>
            )}
            {avisoSenha && (
              <p role="status" className="text-ok-600 text-sm">
                {avisoSenha}
              </p>
            )}

            <div>
              <Botao type="submit" disabled={!podeTrocarSenha}>
                <KeyRound className="size-4" aria-hidden />
                {senha.isPending ? 'Trocando…' : 'Trocar senha'}
              </Botao>
            </div>

            {/*
              Dito ANTES de trocar, não só depois: quem está trocando a senha
              num computador emprestado precisa saber que vai cair fora dele.
            */}
            <p className="text-tinta-500 border-tinta-100 border-t pt-3 text-xs">
              Trocar a senha encerra as sessões abertas nos outros aparelhos. Nesta aba
              você continua conectado.
            </p>
          </form>
        </section>
      </div>
    </PaginaShell>
  );
}
